// Painter für Kapitel 1 (Teil 2): Altstadtgasse, Leuchtturm-Laternenraum, Kutterdeck, Kontor der Reederei.
import { P } from './palette.ts';
import { reg, drawWindow, drawDoor, FONT_DISPLAY, FONT_HAND } from './painters.ts';
import { nightSky } from './painters-harbor.ts';
import { type Ctx, Rng, shade, mix, rgba, vgrad, hgrad, radial, glow, dabs, roundRect, poly, woodGrain, contactShadow, grain, text, paintedFill } from './paint.ts';

// ---------------------------------------------------------------------------------------------
// Altstadtgasse
// ---------------------------------------------------------------------------------------------

function facade(ctx: Ctx, rng: Rng, x: number, w: number, top: number, base: number, color: string, opts: { timber?: boolean; gable?: 'step' | 'tri' | 'flat' } = {}) {
  ctx.save();
  // Giebel
  ctx.fillStyle = color;
  const g = opts.gable ?? 'tri';
  if (g === 'tri') poly(ctx, [x, top, x + w / 2, top - w * 0.38, x + w, top]);
  else if (g === 'step') {
    const s = w / 5;
    poly(ctx, [x, top, x, top - s, x + s, top - s, x + s, top - 2 * s, x + 2 * s, top - 2 * s, x + 2 * s, top - 3 * s, x + 3 * s, top - 3 * s, x + 3 * s, top - 2 * s, x + 4 * s, top - 2 * s, x + 4 * s, top - s, x + w, top - s, x + w, top]);
  } else ctx.rect(x, top - 20, w, 20);
  ctx.fill();
  ctx.fillRect(x, top, w, base - top);
  // Backstein-/Putzstruktur
  ctx.save();
  ctx.beginPath();
  ctx.rect(x, top - w, w, base - top + w);
  ctx.clip();
  if (opts.timber) {
    ctx.strokeStyle = shade(P.wood0, -0.1);
    ctx.lineWidth = 9;
    for (let yy = top; yy < base; yy += 110) {
      ctx.beginPath();
      ctx.moveTo(x, yy);
      ctx.lineTo(x + w, yy);
      ctx.stroke();
    }
    for (let xx = x; xx <= x + w; xx += w / 4) {
      ctx.beginPath();
      ctx.moveTo(xx, top);
      ctx.lineTo(xx, base);
      ctx.stroke();
    }
    ctx.lineWidth = 6;
    for (let xx = x; xx < x + w; xx += w / 4) {
      ctx.beginPath();
      ctx.moveTo(xx, top + 110);
      ctx.lineTo(xx + w / 4, top + 220);
      ctx.stroke();
    }
  } else {
    for (let yy = top - w; yy < base; yy += 24) {
      for (let xx = x - ((yy / 24) % 2) * 25; xx < x + w; xx += 50) {
        ctx.fillStyle = shade(color, rng.range(-0.25, 0.08));
        ctx.fillRect(xx + 2, yy + 2, 46, 20);
      }
    }
  }
  dabs(ctx, rng, x, top - w * 0.4, w, base - top + w * 0.4, [P.night1, shade(color, -0.3)], { count: 200, alpha: [0.05, 0.14] });
  ctx.restore();
  // Obergeschossfenster
  for (let i = 0; i < 3; i++) {
    const wx = x + w * 0.12 + i * w * 0.29, wy = top + 30;
    const lit = rng.chance(0.4);
    ctx.fillStyle = P.wood0;
    ctx.fillRect(wx - 6, wy - 6, w * 0.2 + 12, 92);
    vgrad(ctx, wx, wy, w * 0.2, 80, lit ? [[0, P.amber2], [1, P.amber0]] : [[0, P.night2], [1, P.night0]]);
    if (lit) glow(ctx, wx + w * 0.1, wy + 40, 70, P.amber2, 0.25);
    ctx.fillStyle = P.wood0;
    ctx.fillRect(wx + w * 0.1 - 2, wy, 4, 80);
  }
  ctx.restore();
  vgrad(ctx, x, top - w * 0.4, w, base - top + w * 0.4, [[0, rgba(P.night0, 0.35)], [1, rgba(P.night0, 0)]]);
}

reg('alley', {
  size: (p) => ({ w: p.width ?? 2400, h: p.height ?? 1080, ax: 0, ay: 0 }),
  draw(ctx, p, rng) {
    const W = p.width ?? 2400, H = p.height ?? 1080;
    const floorY = p.floorY ?? 760;
    nightSky(ctx, rng, W, 520, W * 0.18, 110);
    // Häuserzeile
    const houses: [number, number, string, boolean, 'step' | 'tri' | 'flat'][] = [
      [0, 420, P.red0, false, 'step'],
      [420, 380, P.plaster1, true, 'tri'],
      [800, 460, P.red1, false, 'tri'],
      [1260, 360, P.sea0, false, 'flat'],
      [1620, 440, P.plaster0, true, 'step'],
      [2060, 400, P.red0, false, 'tri'],
    ];
    for (const [x, w, c, timber, gable] of houses) facade(ctx, rng, x, w, 300 - (x % 3) * 20, floorY, shade(c, -0.25), { timber, gable });
    // Durchgang zum Hafen (Torbogen)
    const ax = p.archX ?? 1440;
    ctx.fillStyle = P.night0;
    ctx.beginPath();
    ctx.moveTo(ax - 90, floorY);
    ctx.lineTo(ax - 90, floorY - 260);
    ctx.arc(ax, floorY - 260, 90, Math.PI, 0);
    ctx.lineTo(ax + 90, floorY);
    ctx.fill();
    // Blick durch den Bogen: Wasser und ein Lichtreflex
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(ax - 90, floorY);
    ctx.lineTo(ax - 90, floorY - 260);
    ctx.arc(ax, floorY - 260, 90, Math.PI, 0);
    ctx.lineTo(ax + 90, floorY);
    ctx.clip();
    vgrad(ctx, ax - 90, floorY - 350, 180, 220, [[0, P.night1], [1, P.night2]]);
    vgrad(ctx, ax - 90, floorY - 130, 180, 130, [[0, P.sea0], [1, P.night0]]);
    for (let i = 0; i < 14; i++) {
      ctx.fillStyle = rgba(P.amber2, rng.range(0.2, 0.5));
      ctx.fillRect(ax - 80 + rng.next() * 160, floorY - 120 + rng.next() * 100, rng.range(6, 18), 2);
    }
    ctx.restore();
    ctx.strokeStyle = shade(P.plaster1, -0.2);
    ctx.lineWidth = 16;
    ctx.beginPath();
    ctx.arc(ax, floorY - 260, 98, Math.PI, 0);
    ctx.stroke();
    // Türen
    for (const d of p.doors ?? []) drawDoor(ctx, rng, { ...d, floorY });
    // Pflaster
    vgrad(ctx, 0, floorY, W, H - floorY, [[0, shade(P.iron1, -0.4)], [1, shade(P.iron1, -0.15)]]);
    for (let row = 0; row < 36; row++) {
      const t = row / 36;
      const y = floorY + Math.pow(t, 1.5) * (H - floorY);
      const s = 9 + t * 36;
      for (let x = -s; x < W + s; x += s * 1.9) {
        ctx.fillStyle = shade(P.iron2, rng.range(-0.6, -0.25));
        ctx.beginPath();
        ctx.ellipse(x + (row % 2) * s, y, s * 0.85, s * 0.34, 0, 0, Math.PI * 2);
        ctx.fill();
      }
    }
    // Rinnstein
    ctx.fillStyle = shade(P.iron1, -0.5);
    ctx.fillRect(0, floorY - 6, W, 12);
    grain(ctx, 0, floorY, W, H - floorY, 0.15);
    vgrad(ctx, 0, floorY, W, 80, [[0, rgba(P.night0, 0.55)], [1, rgba(P.night0, 0)]]);
    hgrad(ctx, 0, 0, 200, H, [[0, rgba(P.night0, 0.6)], [1, rgba(P.night0, 0)]]);
    hgrad(ctx, W - 200, 0, 200, H, [[0, rgba(P.night0, 0)], [1, rgba(P.night0, 0.6)]]);
  },
});

/** Ladenfront mit Schaufenster; params: kind 'books'|'kiosk', sign, w */
reg('shopfront', {
  size: (p) => ({ w: p.w ?? 460, h: 380 }),
  draw(ctx, p, rng) {
    const w = p.w ?? 460;
    ctx.fillStyle = P.wood1;
    ctx.fillRect(0, 60, w, 320);
    woodGrain(ctx, rng, 0, 0, w, 64, P.wood0, true);
    ctx.fillStyle = P.paper;
    roundRect(ctx, 20, 10, w - 40, 44, 6);
    ctx.fill();
    text(ctx, p.sign ?? 'Antiquariat', w / 2, 33, `700 28px ${FONT_DISPLAY}`, P.red0);
    // Schaufenster
    const sw = w - 40;
    vgrad(ctx, 20, 80, sw, 200, [[0, P.amber2], [1, P.amber0]]);
    if (p.kind === 'books') {
      for (let shelf = 0; shelf < 3; shelf++) {
        let x = 30;
        const y = 140 + shelf * 64;
        while (x < 20 + sw - 20) {
          const bw = rng.range(10, 22), bh = rng.range(36, 54);
          ctx.fillStyle = rng.pick([P.red1, P.sea1, P.green1, P.wood3, P.night2, P.brass0]);
          ctx.fillRect(x, y - bh, bw, bh);
          ctx.fillStyle = rgba(P.paper, 0.4);
          ctx.fillRect(x + 2, y - bh + 6, bw - 4, 3);
          x += bw + 2;
        }
        ctx.fillStyle = P.wood0;
        ctx.fillRect(24, y, sw - 8, 6);
      }
      // Globus
      ctx.fillStyle = P.sea2;
      ctx.beginPath();
      ctx.arc(w - 80, 120, 24, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = P.green2;
      ctx.beginPath();
      ctx.ellipse(w - 86, 114, 10, 14, 0.4, 0, Math.PI * 2);
      ctx.fill();
    } else {
      // Kiosk: Zeitungen, Bonbongläser
      for (let i = 0; i < 5; i++) {
        ctx.fillStyle = P.paper;
        ctx.fillRect(34 + i * 74, 110, 60, 80);
        ctx.fillStyle = rgba(P.ink, 0.6);
        for (let l = 0; l < 6; l++) ctx.fillRect(40 + i * 74, 122 + l * 10, 48, 3);
      }
      for (let i = 0; i < 4; i++) {
        ctx.fillStyle = rgba(P.white, 0.3);
        roundRect(ctx, 40 + i * 90, 210, 60, 60, 10);
        ctx.fill();
        for (let k = 0; k < 8; k++) {
          ctx.fillStyle = rng.pick([P.red2, P.amber2, P.green2, P.sea2]);
          ctx.beginPath();
          ctx.arc(52 + i * 90 + rng.next() * 36, 236 + rng.next() * 28, 6, 0, Math.PI * 2);
          ctx.fill();
        }
      }
    }
    ctx.fillStyle = rgba(P.white, 0.08);
    poly(ctx, [20, 80, 140, 80, 20, 220]);
    ctx.fill();
    ctx.strokeStyle = P.wood0;
    ctx.lineWidth = 8;
    ctx.strokeRect(20, 80, sw, 200);
    glow(ctx, w / 2, 180, w * 0.6, P.amber2, 0.3);
    // Sockel
    woodGrain(ctx, rng, 0, 290, w, 90, P.wood2, true);
  },
});

/** Litfaßsäule mit Plakaten */
reg('adcolumn', {
  size: () => ({ w: 160, h: 420 }),
  draw(ctx, p, rng) {
    contactShadow(ctx, 80, 414, 150, 24, 0.55);
    ctx.fillStyle = P.green0;
    ctx.fillRect(20, 50, 120, 360);
    poly(ctx, [10, 50, 80, 0, 150, 50]);
    ctx.fill();
    for (let i = 0; i < 4; i++) {
      ctx.fillStyle = rng.pick([P.paper, P.amber2, P.red2, P.sea2]);
      ctx.fillRect(28 + (i % 2) * 54, 70 + Math.floor(i / 2) * 140, 50, 120);
    }
    text(ctx, 'QUIZ', 55, 120, `800 18px ${FONT_DISPLAY}`, P.red0);
    text(ctx, 'Fr. 20 Uhr', 55, 145, `600 14px ${FONT_HAND}`, P.ink);
    hgrad(ctx, 20, 50, 120, 360, [[0, rgba(P.night0, 0.5)], [0.4, rgba(P.amber3, 0.1)], [1, rgba(P.night0, 0.6)]]);
  },
});

/** Katze (sitzend) */
reg('cat', {
  size: () => ({ w: 90, h: 90 }),
  draw(ctx) {
    ctx.fillStyle = P.ink;
    ctx.beginPath();
    ctx.ellipse(45, 66, 26, 22, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.beginPath();
    ctx.arc(45, 34, 16, 0, Math.PI * 2);
    ctx.fill();
    poly(ctx, [32, 26, 34, 10, 42, 22]);
    ctx.fill();
    poly(ctx, [58, 26, 56, 10, 48, 22]);
    ctx.fill();
    ctx.strokeStyle = P.ink;
    ctx.lineWidth = 6;
    ctx.beginPath();
    ctx.moveTo(68, 80);
    ctx.quadraticCurveTo(90, 76, 82, 50);
    ctx.stroke();
    ctx.fillStyle = P.amber2;
    ctx.fillRect(38, 31, 5, 4);
    ctx.fillRect(48, 31, 5, 4);
  },
});

// ---------------------------------------------------------------------------------------------
// Leuchtturm – Laternenraum
// ---------------------------------------------------------------------------------------------

reg('lamproom', {
  size: (p) => ({ w: p.width ?? 1920, h: p.height ?? 1080, ax: 0, ay: 0 }),
  draw(ctx, p, rng) {
    const W = p.width ?? 1920, H = p.height ?? 1080, floorY = p.floorY ?? 760;
    // Panoramafenster mit Nacht-Meer
    nightSky(ctx, rng, W, floorY, W * 0.8, 160);
    vgrad(ctx, 0, 480, W, floorY - 480, [[0, P.night2], [0.2, P.sea0], [1, P.night0]]);
    for (let i = 0; i < 200; i++) {
      ctx.fillStyle = rgba(P.night3, rng.range(0.1, 0.35));
      ctx.fillRect(rng.next() * W, 480 + Math.pow(rng.next(), 0.7) * (floorY - 480), rng.range(10, 50), 2);
    }
    // ferne Küste mit Lichtern
    ctx.fillStyle = P.night0;
    ctx.beginPath();
    ctx.moveTo(0, 490);
    for (let x = 0; x <= W; x += 60) ctx.lineTo(x, 485 - Math.sin(x * 0.01) * 6 - rng.range(0, 8));
    ctx.lineTo(W, 500);
    ctx.lineTo(0, 500);
    ctx.fill();
    for (let i = 0; i < 30; i++) {
      ctx.fillStyle = P.amber2;
      ctx.fillRect(rng.next() * W, 480 + rng.range(0, 10), 3, 3);
    }
    // Fensterstreben (Polygon-Laterne)
    for (let i = 0; i <= 8; i++) {
      const x = (i / 8) * W;
      ctx.fillStyle = P.iron0;
      ctx.fillRect(x - 14, 60, 28, floorY - 60);
      ctx.fillStyle = rgba(P.iron2, 0.4);
      ctx.fillRect(x - 14, 60, 6, floorY - 60);
    }
    ctx.fillStyle = P.iron0;
    ctx.fillRect(0, 300, W, 14);
    // Kuppel/Decke
    vgrad(ctx, 0, 0, W, 80, [[0, P.ink], [1, P.iron0]]);
    // Brüstung
    ctx.fillStyle = shade(P.red0, -0.2);
    ctx.fillRect(0, floorY - 140, W, 140);
    for (let x = 0; x < W; x += 96) {
      ctx.fillStyle = shade(P.red0, -0.4);
      ctx.fillRect(x + 4, floorY - 134, 88, 128);
    }
    ctx.fillStyle = P.brass1;
    ctx.fillRect(0, floorY - 146, W, 10);
    // Boden: Riffelblech
    vgrad(ctx, 0, floorY, W, H - floorY, [[0, P.iron0], [1, P.iron1]]);
    for (let y = floorY + 10; y < H; y += 26) {
      for (let x = (y % 52) * 0.5; x < W; x += 40) {
        ctx.strokeStyle = rgba(P.iron2, 0.35);
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.moveTo(x, y);
        ctx.lineTo(x + 14, y - 6);
        ctx.stroke();
      }
    }
    vgrad(ctx, 0, floorY, W, 90, [[0, rgba(P.night0, 0.6)], [1, rgba(P.night0, 0)]]);
    grain(ctx, 0, 0, W, H, 0.08);
  },
});

/** Fresnel-Linse (großes Leuchtfeuer) */
reg('fresnel', {
  size: () => ({ w: 420, h: 560 }),
  draw(ctx, p) {
    contactShadow(ctx, 210, 552, 380, 40, 0.6);
    // Sockel
    ctx.fillStyle = P.brass0;
    poly(ctx, [120, 560, 300, 560, 270, 420, 150, 420]);
    ctx.fill();
    ctx.fillStyle = P.brass1;
    ctx.fillRect(140, 410, 140, 18);
    // Linsenkörper
    const lit = p.state !== 'off';
    const g = ctx.createRadialGradient(210, 230, 10, 210, 230, 190);
    g.addColorStop(0, lit ? P.amber3 : P.sea2);
    g.addColorStop(0.35, lit ? rgba(P.amber2, 0.8) : P.sea1);
    g.addColorStop(1, rgba(lit ? P.amber0 : P.night2, 0.45));
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.ellipse(210, 230, 170, 190, 0, 0, Math.PI * 2);
    ctx.fill();
    // Prismenringe
    ctx.strokeStyle = rgba(P.white, 0.45);
    ctx.lineWidth = 3;
    for (let i = 1; i <= 7; i++) {
      ctx.beginPath();
      ctx.ellipse(210, 230, 170 * (i / 7.5), 26 + i * 4, 0, 0, Math.PI * 2);
      ctx.stroke();
    }
    for (let y = 60; y < 410; y += 26) {
      ctx.strokeStyle = rgba(P.brass2, 0.55);
      ctx.lineWidth = 4;
      const half = Math.sqrt(Math.max(0, 1 - Math.pow((y - 230) / 190, 2))) * 170;
      ctx.beginPath();
      ctx.moveTo(210 - half, y);
      ctx.lineTo(210 + half, y);
      ctx.stroke();
    }
    ctx.strokeStyle = P.brass0;
    ctx.lineWidth = 8;
    ctx.beginPath();
    ctx.moveTo(210, 40);
    ctx.lineTo(210, 420);
    ctx.stroke();
    if (lit) glow(ctx, 210, 230, 220, P.amber3, 0.3);
  },
});

/** Schreibpult mit Logbuch und Funkgerät */
reg('desk', {
  size: (p) => ({ w: p.w ?? 360, h: 260 }),
  draw(ctx, p, rng) {
    const w = p.w ?? 360;
    contactShadow(ctx, w / 2, 254, w, 30, 0.55);
    woodGrain(ctx, rng, 0, 90, w, 26, P.wood3, true);
    ctx.fillStyle = P.wood1;
    ctx.fillRect(10, 116, 24, 140);
    ctx.fillRect(w - 34, 116, 24, 140);
    woodGrain(ctx, rng, w - 140, 116, 110, 120, P.wood2, true);
    ctx.fillStyle = P.brass1;
    ctx.fillRect(w - 90, 150, 20, 6);
    ctx.fillRect(w - 90, 200, 20, 6);
    if (p.radio) {
      ctx.fillStyle = P.iron1;
      roundRect(ctx, 30, 10, 140, 80, 8);
      ctx.fill();
      ctx.fillStyle = P.amber2;
      ctx.fillRect(44, 24, 60, 22);
      for (let i = 0; i < 3; i++) {
        ctx.fillStyle = P.iron0;
        ctx.beginPath();
        ctx.arc(60 + i * 36, 66, 10, 0, Math.PI * 2);
        ctx.fill();
      }
      glow(ctx, 74, 35, 50, P.amber2, 0.3);
    }
    if (p.book) {
      ctx.fillStyle = P.red0;
      ctx.fillRect(w / 2 - 10, 66, 120, 26);
      ctx.fillStyle = P.paper;
      ctx.fillRect(w / 2 - 6, 70, 112, 8);
    }
    if (p.lamp) {
      ctx.strokeStyle = P.brass1;
      ctx.lineWidth = 5;
      ctx.beginPath();
      ctx.moveTo(w - 60, 90);
      ctx.lineTo(w - 80, 30);
      ctx.lineTo(w - 40, 10);
      ctx.stroke();
      ctx.fillStyle = P.green1;
      poly(ctx, [w - 60, 0, w - 10, 20, w - 30, 36]);
      ctx.fill();
      glow(ctx, w - 30, 40, 90, P.amber2, 0.4);
    }
  },
});

/** Wendeltreppe (Abgang) */
reg('spiral', {
  size: () => ({ w: 260, h: 380 }),
  draw(ctx, p, rng) {
    ctx.fillStyle = P.night0;
    ctx.beginPath();
    ctx.ellipse(130, 330, 120, 40, 0, 0, Math.PI * 2);
    ctx.fill();
    for (let i = 0; i < 9; i++) {
      const a = i * 0.7;
      const x = 130 + Math.cos(a) * 80, y = 330 - i * 10 + Math.sin(a) * 18;
      ctx.fillStyle = shade(P.iron1, -0.1 + (i % 2) * 0.1);
      poly(ctx, [130, y - 10, x, y, x + 20, y + 14, 130, y + 6]);
      ctx.fill();
    }
    ctx.fillStyle = P.iron0;
    ctx.fillRect(122, 120, 16, 220);
    ctx.strokeStyle = P.brass1;
    ctx.lineWidth = 6;
    ctx.beginPath();
    ctx.arc(130, 200, 110, Math.PI * 1.05, Math.PI * 1.95);
    ctx.stroke();
    void rng;
  },
});

// ---------------------------------------------------------------------------------------------
// Kutterdeck
// ---------------------------------------------------------------------------------------------

reg('deck', {
  size: (p) => ({ w: p.width ?? 2200, h: p.height ?? 1080, ax: 0, ay: 0 }),
  draw(ctx, p, rng) {
    const W = p.width ?? 2200, H = p.height ?? 1080;
    const railY = p.railY ?? 560, floorY = p.floorY ?? 700;
    nightSky(ctx, rng, W, railY, W * 0.3, 120);
    // Hafen im Hintergrund
    ctx.fillStyle = P.night1;
    for (let i = 0; i < 18; i++) {
      const bx = (i / 18) * W, bh = rng.range(30, 120);
      ctx.fillRect(bx, railY - 40 - bh, W / 18 + 4, bh + 40);
      for (let k = 0; k < 3; k++) if (rng.chance(0.4)) {
        ctx.fillStyle = P.amber2;
        ctx.fillRect(bx + rng.range(5, W / 18 - 8), railY - rng.range(50, 30 + bh), 3, 4);
        ctx.fillStyle = P.night1;
      }
    }
    // Kräne
    ctx.strokeStyle = P.night0;
    ctx.lineWidth = 8;
    for (const cx of [W * 0.2, W * 0.78]) {
      ctx.beginPath();
      ctx.moveTo(cx, railY - 40);
      ctx.lineTo(cx, railY - 320);
      ctx.lineTo(cx + 220, railY - 300);
      ctx.stroke();
      ctx.fillStyle = P.red2;
      ctx.fillRect(cx - 4, railY - 326, 8, 8);
    }
    vgrad(ctx, 0, railY - 40, W, 40, [[0, P.sea0], [1, P.night0]]);
    // Reling
    ctx.fillStyle = P.paper;
    ctx.fillRect(0, railY, W, 16);
    ctx.fillStyle = shade(P.sea1, -0.1);
    ctx.fillRect(0, railY + 16, W, floorY - railY - 16);
    for (let x = 0; x < W; x += 140) {
      ctx.fillStyle = shade(P.sea1, -0.35);
      ctx.fillRect(x, railY + 16, 10, floorY - railY - 16);
    }
    dabs(ctx, rng, 0, railY + 16, W, floorY - railY, [P.wood3, P.night1], { count: 300, alpha: [0.05, 0.15] });
    // Deck: Planken
    vgrad(ctx, 0, floorY, W, H - floorY, [[0, shade(P.wood3, -0.35)], [1, P.wood3]]);
    for (let i = 0; i < 18; i++) {
      const y = floorY + Math.pow(i / 18, 1.4) * (H - floorY);
      ctx.strokeStyle = rgba(P.wood0, 0.6);
      ctx.lineWidth = 1 + i * 0.15;
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(W, y);
      ctx.stroke();
      for (let k = 0; k < 6; k++) {
        const x = rng.next() * W;
        ctx.beginPath();
        ctx.moveTo(x, y);
        ctx.lineTo(x, y + 6 + i * 2);
        ctx.stroke();
      }
    }
    dabs(ctx, rng, 0, floorY, W, H - floorY, [P.wood4, P.wood1], { count: 700, size: [3, 14], stretch: 5 });
    grain(ctx, 0, floorY, W, H - floorY, 0.14);
    vgrad(ctx, 0, floorY, W, 70, [[0, rgba(P.night0, 0.5)], [1, rgba(P.night0, 0)]]);
  },
});

/** Steuerhaus von hinten mit Tür */
reg('wheelhouse', {
  size: () => ({ w: 520, h: 560 }),
  draw(ctx, p, rng) {
    contactShadow(ctx, 260, 552, 520, 40, 0.6);
    paintedFill(ctx, rng, () => ctx.rect(20, 90, 480, 470), [20, 90, 480, 470], P.paper, { dark: 0.4 });
    ctx.fillStyle = P.red1;
    ctx.fillRect(0, 60, 520, 36);
    for (let i = 0; i < 3; i++) {
      ctx.fillStyle = P.night1;
      roundRect(ctx, 50 + i * 150, 130, 110, 110, 10);
      ctx.fill();
      ctx.fillStyle = rgba(P.amber2, i === 1 ? 0.5 : 0.15);
      roundRect(ctx, 56 + i * 150, 136, 98, 98, 8);
      ctx.fill();
    }
    // Tür
    ctx.fillStyle = shade(P.sea1, -0.1);
    roundRect(ctx, 200, 280, 120, 280, 8);
    ctx.fill();
    ctx.fillStyle = P.brass1;
    ctx.beginPath();
    ctx.arc(300, 420, 8, 0, Math.PI * 2);
    ctx.fill();
    // Rettungsring
    ctx.strokeStyle = P.red1;
    ctx.lineWidth = 18;
    ctx.beginPath();
    ctx.arc(420, 380, 44, 0, Math.PI * 2);
    ctx.stroke();
    ctx.strokeStyle = P.white;
    ctx.setLineDash([22, 22]);
    ctx.beginPath();
    ctx.arc(420, 380, 44, 0, Math.PI * 2);
    ctx.stroke();
    ctx.setLineDash([]);
    // Antennen
    ctx.strokeStyle = P.iron1;
    ctx.lineWidth = 4;
    ctx.beginPath();
    ctx.moveTo(100, 60);
    ctx.lineTo(100, 0);
    ctx.moveTo(420, 60);
    ctx.lineTo(440, 10);
    ctx.stroke();
    glow(ctx, 260, 190, 160, P.amber2, 0.25);
  },
});

/** Flaggenkiste */
reg('flagbox', {
  size: () => ({ w: 240, h: 170 }),
  draw(ctx, p, rng) {
    contactShadow(ctx, 120, 164, 230, 26, 0.55);
    woodGrain(ctx, rng, 10, 60, 220, 104, P.wood2, true);
    ctx.fillStyle = P.brass1;
    ctx.fillRect(10, 60, 220, 8);
    const open = p.state === 'open';
    if (open) {
      const cols = [P.red1, P.amber2, P.sea1, P.white, P.night2];
      for (let i = 0; i < 6; i++) {
        ctx.fillStyle = cols[i % cols.length];
        poly(ctx, [24 + i * 32, 60, 52 + i * 32, 60, 46 + i * 32, 10 + (i % 2) * 14, 30 + i * 32, 18]);
        ctx.fill();
      }
    } else {
      woodGrain(ctx, rng, 6, 40, 228, 24, P.wood3, true);
    }
    text(ctx, 'FLAGGEN', 120, 116, `700 22px ${FONT_DISPLAY}`, rgba(P.paper, 0.7));
  },
});

/** Netzhaufen */
reg('nets', {
  size: () => ({ w: 300, h: 160 }),
  draw(ctx, p, rng) {
    contactShadow(ctx, 150, 154, 290, 26, 0.5);
    ctx.fillStyle = P.green1;
    ctx.beginPath();
    ctx.ellipse(150, 110, 140, 50, 0, Math.PI, 0);
    ctx.lineTo(290, 150);
    ctx.lineTo(10, 150);
    ctx.fill();
    ctx.strokeStyle = rgba(P.green2, 0.8);
    ctx.lineWidth = 1.5;
    for (let i = 0; i < 40; i++) {
      ctx.beginPath();
      ctx.moveTo(rng.range(20, 280), rng.range(70, 150));
      ctx.lineTo(rng.range(20, 280), rng.range(70, 150));
      ctx.stroke();
    }
    for (let i = 0; i < 6; i++) {
      ctx.fillStyle = P.amber1;
      ctx.beginPath();
      ctx.arc(rng.range(40, 260), rng.range(90, 140), 10, 0, Math.PI * 2);
      ctx.fill();
    }
  },
});

/** Mast mit Signalflaggen-Leine */
reg('mast', {
  size: () => ({ w: 500, h: 700 }),
  draw(ctx, p, rng) {
    ctx.fillStyle = P.wood1;
    ctx.fillRect(240, 0, 22, 700);
    ctx.strokeStyle = rgba(P.paper, 0.7);
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(251, 20);
    ctx.lineTo(0, 300);
    ctx.moveTo(251, 20);
    ctx.lineTo(500, 320);
    ctx.stroke();
    const cols = [P.red1, P.amber2, P.sea1, P.white, P.night2, P.green1];
    for (let i = 0; i < 8; i++) {
      const t = (i + 1) / 9;
      const x = 251 + (500 - 251) * t, y = 20 + 300 * t;
      ctx.fillStyle = cols[i % cols.length];
      poly(ctx, [x - 14, y + 2, x + 14, y + 4, x, y + 34]);
      ctx.fill();
    }
    void rng;
  },
});

// ---------------------------------------------------------------------------------------------
// Kontor der Reederei
// ---------------------------------------------------------------------------------------------

reg('office', {
  size: (p) => ({ w: p.width ?? 1920, h: p.height ?? 1080, ax: 0, ay: 0 }),
  draw(ctx, p, rng) {
    const W = p.width ?? 1920, H = p.height ?? 1080, floorY = p.floorY ?? 720;
    // dunkle Holzvertäfelung bis oben
    for (let x = 0; x < W; x += 120) woodGrain(ctx, rng, x, 0, 120, floorY, shade(P.wood1, rng.range(-0.1, 0.05)), false);
    for (let x = 0; x < W; x += 240) {
      ctx.strokeStyle = rgba(P.ink, 0.5);
      ctx.lineWidth = 4;
      ctx.strokeRect(x + 20, 80, 200, 260);
      ctx.strokeRect(x + 20, 380, 200, floorY - 420);
    }
    ctx.fillStyle = P.brass0;
    ctx.fillRect(0, 360, W, 8);
    for (const win of p.windows ?? []) drawWindow(ctx, rng, win);
    // Teppich-Boden
    vgrad(ctx, 0, floorY, W, H - floorY, [[0, shade(P.red0, -0.4)], [1, P.red0]]);
    ctx.strokeStyle = rgba(P.brass1, 0.5);
    ctx.lineWidth = 6;
    ctx.beginPath();
    ctx.moveTo(W * 0.18, floorY + 30);
    ctx.lineTo(W * 0.82, floorY + 30);
    ctx.lineTo(W * 0.95, H);
    ctx.moveTo(W * 0.18, floorY + 30);
    ctx.lineTo(W * 0.05, H);
    ctx.stroke();
    dabs(ctx, rng, 0, floorY, W, H - floorY, [P.red1, P.night0], { count: 600, alpha: [0.04, 0.12] });
    grain(ctx, 0, floorY, W, H - floorY, 0.14);
    vgrad(ctx, 0, floorY, W, 90, [[0, rgba(P.night0, 0.6)], [1, rgba(P.night0, 0)]]);
    for (const d of p.doors ?? []) drawDoor(ctx, rng, { ...d, floorY });
    hgrad(ctx, 0, 0, 260, H, [[0, rgba(P.night0, 0.6)], [1, rgba(P.night0, 0)]]);
    hgrad(ctx, W - 260, 0, 260, H, [[0, rgba(P.night0, 0)], [1, rgba(P.night0, 0.6)]]);
  },
});

/** Aktenschrank mit Schubladen */
reg('cabinet', {
  size: () => ({ w: 200, h: 380 }),
  draw(ctx, p, rng) {
    contactShadow(ctx, 100, 374, 190, 26, 0.55);
    woodGrain(ctx, rng, 0, 0, 200, 374, P.wood2, false);
    for (let i = 0; i < 4; i++) {
      ctx.strokeStyle = rgba(P.ink, 0.6);
      ctx.lineWidth = 3;
      ctx.strokeRect(14, 14 + i * 88, 172, 78);
      ctx.fillStyle = P.brass1;
      ctx.fillRect(84, 46 + i * 88, 32, 10);
      ctx.fillStyle = P.paper;
      ctx.fillRect(80, 24 + i * 88, 40, 16);
      text(ctx, ['A–F', 'G–M', 'N–S', 'T–Z'][i], 100, 32 + i * 88, `600 12px ${FONT_DISPLAY}`, P.ink);
    }
    if (p.state === 'open') {
      ctx.fillStyle = P.wood1;
      ctx.fillRect(0, 186, 200, 70);
      ctx.fillStyle = P.paper;
      for (let k = 0; k < 6; k++) ctx.fillRect(14 + k * 30, 176, 24, 20);
    }
  },
});

/** Globus auf Ständer */
reg('globe', {
  size: () => ({ w: 180, h: 300 }),
  draw(ctx, p, rng) {
    contactShadow(ctx, 90, 294, 130, 22, 0.5);
    ctx.fillStyle = P.wood1;
    ctx.fillRect(84, 180, 12, 110);
    poly(ctx, [40, 294, 140, 294, 96, 270, 84, 270]);
    ctx.fill();
    ctx.fillStyle = P.sea1;
    ctx.beginPath();
    ctx.arc(90, 100, 76, 0, Math.PI * 2);
    ctx.fill();
    for (let i = 0; i < 5; i++) {
      ctx.fillStyle = mix(P.plaster2, P.green2, rng.next());
      ctx.beginPath();
      ctx.ellipse(rng.range(40, 140), rng.range(50, 150), rng.range(10, 26), rng.range(8, 20), rng.next() * 3, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.strokeStyle = P.brass1;
    ctx.lineWidth = 6;
    ctx.beginPath();
    ctx.arc(90, 100, 86, -Math.PI * 0.6, Math.PI * 0.6);
    ctx.stroke();
    radial(ctx, 64, 74, 70, [[0, rgba(P.white, 0.3)], [1, rgba(P.white, 0)]]);
  },
});

/** Schiffsgemälde (Reederei-Flotte) */
reg('shippainting', {
  size: () => ({ w: 300, h: 200 }),
  draw(ctx, p, rng) {
    ctx.fillStyle = P.brass1;
    ctx.fillRect(0, 0, 300, 200);
    ctx.fillStyle = P.brass0;
    ctx.fillRect(10, 10, 280, 180);
    vgrad(ctx, 16, 16, 268, 168, [[0, P.plaster2], [0.55, P.sea2], [1, P.sea0]]);
    ctx.fillStyle = P.ink;
    poly(ctx, [60, 120, 240, 120, 220, 150, 80, 150]);
    ctx.fill();
    ctx.fillRect(140, 60, 8, 60);
    ctx.fillStyle = P.paper;
    poly(ctx, [148, 64, 200, 110, 148, 110]);
    ctx.fill();
    ctx.fillStyle = P.red1;
    ctx.fillRect(150, 90, 30, 16);
    radial(ctx, 150, 100, 180, [[0, rgba(P.amber1, 0.1)], [1, rgba(P.wood0, 0.45)]]);
    grain(ctx, 16, 16, 268, 168, 0.2);
    void rng;
  },
});

/** Großer Schreibtisch des Reeders */
reg('bigdesk', {
  size: () => ({ w: 560, h: 280 }),
  draw(ctx, p, rng) {
    contactShadow(ctx, 280, 272, 560, 40, 0.6);
    woodGrain(ctx, rng, 0, 80, 560, 30, P.wood3, true);
    for (const x of [20, 380]) {
      woodGrain(ctx, rng, x, 110, 160, 160, P.wood2, true);
      for (let i = 0; i < 3; i++) {
        ctx.strokeStyle = rgba(P.ink, 0.5);
        ctx.strokeRect(x + 10, 118 + i * 50, 140, 42);
        ctx.fillStyle = P.brass1;
        ctx.fillRect(x + 70, 136 + i * 50, 20, 6);
      }
    }
    // Papiere, Tintenfass, Stempel
    ctx.fillStyle = P.paper;
    ctx.save();
    ctx.translate(240, 70);
    ctx.rotate(-0.05);
    ctx.fillRect(0, 0, 90, 16);
    ctx.restore();
    ctx.fillStyle = P.ink;
    ctx.fillRect(360, 50, 26, 32);
    ctx.fillStyle = P.brass1;
    ctx.fillRect(420, 56, 16, 26);
    ctx.fillStyle = P.red1;
    ctx.fillRect(414, 48, 28, 10);
    // grüne Bankerlampe
    ctx.fillStyle = P.brass1;
    ctx.fillRect(110, 30, 8, 52);
    ctx.fillStyle = P.green1;
    roundRect(ctx, 70, 10, 90, 30, 14);
    ctx.fill();
    glow(ctx, 114, 50, 140, P.amber2, 0.4);
  },
});

/** Wandtafel: Schiffsregister */
reg('register', {
  size: () => ({ w: 300, h: 220 }),
  draw(ctx, p, rng) {
    woodGrain(ctx, rng, 0, 0, 300, 220, P.wood1, false);
    ctx.fillStyle = P.night1;
    ctx.fillRect(14, 14, 272, 192);
    text(ctx, 'Flottenregister', 150, 36, `700 20px ${FONT_DISPLAY}`, P.brass2);
    for (let i = 0; i < 5; i++) {
      ctx.fillStyle = rgba(P.paper, 0.75);
      ctx.fillRect(30, 60 + i * 28, 180, 4);
      ctx.fillRect(230, 60 + i * 28, 40, 4);
    }
  },
});

// Inventar-Icons Teil 2
reg('icon2', {
  size: () => ({ w: 96, h: 96, ax: 48, ay: 48 }),
  draw(ctx, p) {
    const k = p.kind;
    if (k === 'logbook') {
      ctx.fillStyle = P.red0;
      roundRect(ctx, 22, 16, 52, 66, 4);
      ctx.fill();
      ctx.fillStyle = P.paper;
      ctx.fillRect(70, 20, 6, 58);
      ctx.fillStyle = P.brass2;
      text(ctx, 'LOG', 48, 48, `800 14px ${FONT_DISPLAY}`, P.brass2);
    } else if (k === 'map1' || k === 'map2' || k === 'map3' || k === 'map') {
      ctx.fillStyle = P.paper;
      const full = k === 'map';
      if (full) {
        ctx.fillRect(14, 20, 68, 56);
      } else {
        const off = { map1: 0, map2: 1, map3: 2 }[k as 'map1']!;
        poly(ctx, [20 + off * 4, 22, 70, 18 + off * 6, 76, 72, 24, 78 - off * 4]);
        ctx.fill();
      }
      ctx.strokeStyle = P.red1;
      ctx.lineWidth = 2;
      ctx.setLineDash([4, 4]);
      ctx.beginPath();
      ctx.moveTo(26, 64);
      ctx.quadraticCurveTo(48, 30, 66, 44);
      ctx.stroke();
      ctx.setLineDash([]);
      if (full) {
        ctx.strokeStyle = P.red1;
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.moveTo(60, 38);
        ctx.lineTo(72, 50);
        ctx.moveTo(72, 38);
        ctx.lineTo(60, 50);
        ctx.stroke();
      }
    } else if (k === 'crowbar') {
      ctx.strokeStyle = P.red1;
      ctx.lineWidth = 8;
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.moveTo(26, 78);
      ctx.lineTo(66, 22);
      ctx.quadraticCurveTo(74, 12, 80, 22);
      ctx.stroke();
    } else if (k === 'trophy') {
      const g = ctx.createLinearGradient(20, 0, 76, 0);
      g.addColorStop(0, P.iron2);
      g.addColorStop(0.5, P.white);
      g.addColorStop(1, P.iron2);
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.moveTo(28, 18);
      ctx.lineTo(68, 18);
      ctx.quadraticCurveTo(68, 56, 48, 60);
      ctx.quadraticCurveTo(28, 56, 28, 18);
      ctx.fill();
      ctx.fillRect(44, 58, 8, 14);
      ctx.fillRect(32, 72, 32, 8);
      ctx.strokeStyle = P.iron2;
      ctx.lineWidth = 4;
      ctx.beginPath();
      ctx.arc(26, 32, 9, Math.PI * 0.5, Math.PI * 1.5);
      ctx.arc(70, 32, 9, Math.PI * 1.5, Math.PI * 0.5);
      ctx.stroke();
      glow(ctx, 48, 38, 40, P.amber3, 0.4);
    } else if (k === 'ticket') {
      ctx.fillStyle = P.amber2;
      roundRect(ctx, 14, 30, 68, 36, 6);
      ctx.fill();
      text(ctx, 'Fähre', 48, 48, `700 15px ${FONT_HAND}`, P.ink);
    }
  },
});

/** Wegweiser */
reg('signpost', {
  size: () => ({ w: 220, h: 300 }),
  draw(ctx, p, rng) {
    contactShadow(ctx, 110, 294, 120, 20, 0.5);
    woodGrain(ctx, rng, 100, 40, 20, 256, P.wood1, false);
    const labels: string[] = p.labels ?? ['Altstadt'];
    labels.forEach((l, i) => {
      const y = 50 + i * 60;
      const dir = i % 2 ? -1 : 1;
      ctx.fillStyle = P.paper;
      const x0 = dir > 0 ? 30 : 10;
      poly(ctx, dir > 0 ? [x0, y, 190, y, 210, y + 22, 190, y + 44, x0, y + 44] : [30, y, 200, y, 200, y + 44, 30, y + 44, 10, y + 22]);
      ctx.fill();
      text(ctx, l, 115, y + 23, `700 22px ${FONT_HAND}`, P.ink);
    });
  },
});
